import React, { useState, useRef, useEffect } from 'react';
import {
  useUpdateReportQuote,
  usePriceReportQuote,
  useSendQuoteRevision,
  useNiceClasses,
  useNiceClassTerms,
  useSuggestGoodsServices,
  useFixPicklistTerms,
  usePricingConfig,
} from '../../hooks/useMarketing';

/**
 * QuoteEditor — the quote card on a report.
 *
 * Covers the whole quote, not just the money: countries and classes are
 * editable here, which is how a client asking to "add class 35 and re-quote
 * me" is served. Prices are recomputed server-side from the same table the
 * wizard used, so nobody hand-types a total.
 *
 * Editing a quote whose report has already been sent creates a REVISION: the
 * new quote carries its own number (Q-XXXXXXXX-R2) and the superseded figures
 * are archived. Saving takes effect immediately — the client's dashboard and
 * the amount they would be charged both follow the pricing record — so the
 * send step tells them about a change that has already happened.
 */
function QuoteEditor({ report, quote, onError }) {
  const updateQuote = useUpdateReportQuote();
  const priceQuote = usePriceReportQuote();
  const sendRevision = useSendQuoteRevision();
  const fixTerms = useFixPicklistTerms();

  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [termsOpenFor, setTermsOpenFor] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [pricing, setPricing] = useState(false);
  const [fixNote, setFixNote] = useState(null);
  // Every scope change fires its own pricing call; only the newest answer may
  // land, or a slow earlier one would leave a price that isn't the draft's
  const priceSeq = useRef(0);

  const { data: pricingConfig } = usePricingConfig(!!draft);
  const knownCountries = pricingConfig?.allCountries || [];
  // A quote can carry a country the price table no longer lists. Show it
  // anyway, or it would be unpriceable *and* invisible — impossible to remove.
  const extraCountries = (draft?.countries || [])
    .filter((id) => knownCountries.length > 0 && !knownCountries.some((c) => c.id === id))
    .map((id) => ({ id, name: `${id.toUpperCase()} (no price configured)`, icon: '⚠️' }));
  const countryOptions = [...knownCountries, ...extraCountries];

  const alreadySent = report.status === 'sent';
  const revision = quote?.revision || 1;
  const currency = (quote?.currency || 'AUD').toUpperCase();
  const money = (n) => `$${Number(n || 0).toLocaleString()}`;

  const startEdit = () => {
    setPricingError(null);
    setPreview(null);
    const next = {
      countries: [...(quote.countries || [])],
      classes: (quote.classes || []).map((c) => ({
        classNumber: c.classNumber ?? c.number,
        className: c.className || c.name || '',
        goodsServices: [...(c.goodsServices || [])],
      })),
      autoPrice: true,
      serviceFeeTotal: quote.serviceFeeTotal ?? '',
      govFeeTotal: quote.govFeeTotal ?? '',
      discount: quote.discount ?? 0,
      total: quote.total ?? '',
    };
    setDraft(next);
    refreshPrice(next);
  };

  /**
   * Server-side price for the draft scope — never computed in the browser.
   * Runs even in manual mode: the fee split and per-country breakdown have to
   * describe the scope being saved, so a scope change re-baselines the
   * numbers the admin is about to override.
   */
  const refreshPrice = (nextDraft) => {
    const seq = ++priceSeq.current;
    setPricingError(null);
    setPricing(true);
    priceQuote.mutate(
      {
        reportId: report.id,
        countries: nextDraft.countries,
        classCount: nextDraft.classes.length,
        currency,
      },
      {
        onSuccess: (res) => {
          if (seq !== priceSeq.current) return; // a newer scope is in flight
          setPreview(res.quote);
          setPricing(false);
          // In manual mode the inputs were seeded from the previous scope;
          // re-baseline them so the admin edits numbers that describe the
          // countries and classes actually being saved
          if (!nextDraft.autoPrice) {
            setDraft((prev) => (prev ? {
              ...prev,
              serviceFeeTotal: res.quote.serviceFeeTotal,
              govFeeTotal: res.quote.govFeeTotal,
              discount: res.quote.discount,
              total: res.quote.total,
            } : prev));
          }
        },
        onError: (e) => {
          if (seq !== priceSeq.current) return;
          setPricingError(e.response?.data?.error || e.message);
          setPricing(false);
        },
      }
    );
  };

  const applyDraft = (changes) => {
    const next = { ...draft, ...changes };
    setDraft(next);
    refreshPrice(next);
  };

  const toggleCountry = (id) => {
    const has = draft.countries.includes(id);
    const countries = has ? draft.countries.filter((c) => c !== id) : [...draft.countries, id];
    if (countries.length === 0) return; // a quote needs at least one country
    applyDraft({ countries });
  };

  const addClass = (cls) => {
    if (draft.classes.some((c) => c.classNumber === cls.classNumber)) return;
    applyDraft({
      classes: [...draft.classes, {
        classNumber: cls.classNumber,
        className: cls.shortName || '',
        goodsServices: [],
      }],
    });
    setPickerOpen(false);
  };

  const removeClass = (classNumber) => {
    if (draft.classes.length <= 1) return; // a quote needs at least one class
    applyDraft({ classes: draft.classes.filter((c) => c.classNumber !== classNumber) });
    if (termsOpenFor === classNumber) setTermsOpenFor(null);
  };

  // Class name and goods/services don't affect price, so they skip the recompute
  const editClass = (classNumber, changes) => {
    setDraft((prev) => ({
      ...prev,
      classes: prev.classes.map((c) => (c.classNumber === classNumber ? { ...c, ...changes } : c)),
    }));
  };

  const save = () => {
    const payload = {
      reportId: report.id,
      countries: draft.countries,
      classes: draft.classes,
      autoPrice: !!draft.autoPrice,
      currency,
    };
    if (!draft.autoPrice) {
      payload.serviceFeeTotal = draft.serviceFeeTotal;
      payload.govFeeTotal = draft.govFeeTotal;
      payload.discount = draft.discount;
      payload.total = draft.total;
    }
    updateQuote.mutate(payload, {
      onSuccess: () => {
        setDraft(null);
        setPreview(null);
      },
      onError: (e) => onError(e.response?.data?.error || e.message),
    });
  };

  /**
   * One click from "this wording costs us $150/class" to a reviewable fix:
   * open the editor with picklist replacements already applied, and say what
   * changed and what still needs a human. Nothing is saved until Save.
   */
  const runFixTerms = () => {
    setFixNote(null);
    fixTerms.mutate(
      { reportId: report.id },
      {
        onSuccess: (res) => {
          const byClass = new Map((res.classes || []).map((c) => [c.classNumber, c.replacements || []]));
          let applied = 0;
          const unresolved = [];

          const next = {
            countries: [...(quote.countries || [])],
            classes: (quote.classes || []).map((c) => {
              const classNumber = c.classNumber ?? c.number;
              const replacements = byClass.get(classNumber) || [];
              const terms = [...(c.goodsServices || [])];
              for (const r of replacements) {
                const at = terms.indexOf(r.term);
                if (at === -1) continue;
                if (r.replacement) {
                  terms[at] = r.replacement;
                  applied += 1;
                } else {
                  unresolved.push(`class ${classNumber}: "${r.term}"`);
                }
              }
              return {
                classNumber,
                className: c.className || c.name || '',
                goodsServices: [...new Set(terms)],
              };
            }),
            autoPrice: !quote.manualPrice,
            serviceFeeTotal: quote.serviceFeeTotal ?? '',
            govFeeTotal: quote.govFeeTotal ?? '',
            discount: quote.discount ?? 0,
            total: quote.total ?? '',
          };

          setPreview(null);
          setPricingError(null);
          setDraft(next);
          refreshPrice(next);
          setFixNote({
            applied,
            unresolved,
            text: applied > 0
              ? `Replaced ${applied} term${applied > 1 ? 's' : ''} with picklist wording. Check them, then save.`
              : 'No picklist wording preserved the meaning of these terms, so nothing was changed.',
          });
        },
        onError: (e) => onError(e.response?.data?.error || e.message),
      }
    );
  };

  const doSendRevision = () => {
    sendRevision.mutate(
      { reportId: report.id },
      {
        onSuccess: (res) => {
          setConfirmSend(false);
          setSendResult({
            quoteNumber: res?.quoteNumber || '',
            email: res?.email || report.contact?.email || 'the client',
          });
        },
        onError: (e) => {
          setConfirmSend(false);
          onError(e.response?.data?.error || e.message);
        },
      }
    );
  };

  const shownTotal = draft?.autoPrice && preview ? preview.total : Number(draft?.total);
  // An unsent revision is refined in place rather than renumbered, so the
  // label has to match what the server will actually do
  const nextRevision = report.quoteRevisionPending ? revision : revision + 1;

  return (
    <div className="mkt-card" style={{ marginBottom: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#475569' }}>
          Quote {quote?.quoteStatus === 'confirmed'
            ? <span style={{ color: '#047857' }}>· confirmed</span>
            : <span style={{ color: '#b45309' }}>· estimate</span>}
          {revision > 1 && <span style={{ color: '#64748b' }}> · revision {revision}</span>}
        </h3>
        {quote && !draft && (
          <button className="mkt-btn mkt-btn-secondary" onClick={startEdit}>
            {alreadySent ? 'Revise quote' : 'Edit quote'}
          </button>
        )}
      </div>

      {!quote ? (
        <p style={{ color: '#94a3b8', fontSize: '13px', margin: '8px 0 0' }}>No pricing record linked.</p>
      ) : draft ? (
        <div style={{ marginTop: '12px' }}>
          {alreadySent && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', marginBottom: '12px' }}>
              This client already has quote {`Q-${String(report.applicationId).slice(0, 8).toUpperCase()}`}
              {revision > 1 ? `-R${revision}` : ''}. Saving issues revision {nextRevision} and takes effect straight away,
              including what they would be charged in their dashboard, so send the update once you are happy with it.
            </div>
          )}

          {fixNote && (
            <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', marginBottom: '12px' }}>
              <p style={{ margin: 0, color: '#0f766e', fontWeight: 600 }}>{fixNote.text}</p>
              {fixNote.unresolved.length > 0 && (
                <p style={{ margin: '4px 0 0', color: '#92400e', fontSize: '12px' }}>
                  Still needs you: {fixNote.unresolved.join(', ')}. Open Goods &amp; services on that class
                  and pick a term, or remove it.
                </p>
              )}
            </div>
          )}

          {/* Countries */}
          <div style={{ marginBottom: '14px' }}>
            <p style={{ fontSize: '12px', fontWeight: 600, color: '#475569', margin: '0 0 6px' }}>Countries</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {countryOptions.map((c) => {
                const on = draft.countries.includes(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => toggleCountry(c.id)}
                    style={{
                      border: `1px solid ${on ? '#0f766e' : '#cbd5e1'}`,
                      background: on ? '#f0fdfa' : 'white',
                      color: on ? '#0f766e' : '#64748b',
                      fontWeight: on ? 600 : 400,
                      borderRadius: '999px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer',
                    }}
                  >
                    {c.icon} {c.name}
                  </button>
                );
              })}
              {countryOptions.length === 0 && (
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Loading countries...</span>
              )}
            </div>
          </div>

          {/* Classes */}
          <div style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p style={{ fontSize: '12px', fontWeight: 600, color: '#475569', margin: '0 0 6px' }}>
                Classes ({draft.classes.length})
              </p>
              <button className="mkt-btn mkt-btn-secondary" style={{ fontSize: '12px' }} onClick={() => setPickerOpen((v) => !v)}>
                {pickerOpen ? 'Close' : '+ Add class'}
              </button>
            </div>

            {pickerOpen && (
              <ClassPicker
                existing={draft.classes.map((c) => c.classNumber)}
                onPick={addClass}
              />
            )}

            {draft.classes.length === 0 && (
              <p style={{ fontSize: '12px', color: '#b45309', margin: '4px 0' }}>
                This quote has no classes recorded. Add at least one before saving.
              </p>
            )}

            <div style={{ display: 'grid', gap: '8px', marginTop: '6px' }}>
              {draft.classes.map((c) => (
                <div key={c.classNumber} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: '#0f766e', fontSize: '13px' }}>Class {c.classNumber}</span>
                    <input
                      value={c.className}
                      onChange={(e) => editClass(c.classNumber, { className: e.target.value })}
                      placeholder="Class name shown on the quote"
                      style={{ flex: 1, minWidth: '180px', padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                    />
                    <button
                      className="mkt-btn mkt-btn-secondary"
                      style={{ fontSize: '12px' }}
                      onClick={() => setTermsOpenFor(termsOpenFor === c.classNumber ? null : c.classNumber)}
                    >
                      Goods &amp; services ({c.goodsServices.length})
                    </button>
                    <button
                      className="mkt-btn mkt-btn-secondary"
                      style={{ fontSize: '12px', color: '#dc2626' }}
                      disabled={draft.classes.length <= 1}
                      onClick={() => removeClass(c.classNumber)}
                    >
                      Remove
                    </button>
                  </div>

                  {termsOpenFor === c.classNumber && (
                    <TermsEditor
                      classNumber={c.classNumber}
                      terms={c.goodsServices}
                      reportId={report.id}
                      onChange={(goodsServices) => editClass(c.classNumber, { goodsServices })}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Price */}
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#475569', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={!!draft.autoPrice}
                onChange={(e) => applyDraft({ autoPrice: e.target.checked })}
              />
              Price automatically from the countries and classes above
            </label>

            {pricingError && (
              <p style={{ color: '#dc2626', fontSize: '13px', margin: '0 0 8px' }}>{pricingError}</p>
            )}

            {draft.autoPrice ? (
              <div style={{ fontSize: '14px', color: '#334155' }}>
                {pricing && <span style={{ color: '#94a3b8' }}>Pricing...</span>}
                {!pricing && !pricingError && preview && (
                  <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
                    <span>Service: {money(preview.serviceFeeTotal)}</span>
                    <span>Gov fees: {money(preview.govFeeTotal)}</span>
                    {preview.discount ? <span>Discount: -{money(preview.discount)}</span> : null}
                    <span style={{ fontWeight: 700, color: '#0f766e' }}>
                      New total: {currency} {money(preview.total)}
                    </span>
                    {quote.total != null && preview.total !== quote.total && (
                      <span style={{ color: '#64748b' }}>
                        (was {currency} {money(quote.total)})
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', maxWidth: '640px' }}>
                {[
                  ['serviceFeeTotal', 'Service fees'],
                  ['govFeeTotal', 'Government fees'],
                  ['discount', 'Discount'],
                  ['total', 'Total (charged)'],
                ].map(([field, label]) => (
                  <label key={field} style={{ fontSize: '12px', color: '#475569' }}>
                    {label}
                    <input
                      type="number"
                      value={draft[field]}
                      onChange={(e) => setDraft((prev) => ({ ...prev, [field]: e.target.value }))}
                      style={{ display: 'block', width: '100%', marginTop: '4px', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                    />
                  </label>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button className="mkt-btn mkt-btn-secondary" onClick={() => { setDraft(null); setPreview(null); setFixNote(null); }}>Cancel</button>
              <button
                className="mkt-btn mkt-btn-primary"
                disabled={
                  updateQuote.isPending ||
                  draft.classes.length === 0 ||
                  !(shownTotal > 0) ||
                  (draft.autoPrice && (pricing || !!pricingError))
                }
                onClick={save}
              >
                {updateQuote.isPending
                  ? 'Saving...'
                  : alreadySent ? `Save revision ${nextRevision}` : 'Confirm quote'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: '8px', fontSize: '14px', color: '#334155' }}>
          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
            <span>Countries: {(quote.countries || []).join(', ').toUpperCase() || '?'}</span>
            {quote.serviceFeeTotal != null && <span>Service: {money(quote.serviceFeeTotal)}</span>}
            {quote.govFeeTotal != null && <span>Gov fees: {money(quote.govFeeTotal)}</span>}
            {quote.discount ? <span>Discount: -{money(quote.discount)}</span> : null}
            <span style={{ fontWeight: 700, color: '#0f766e' }}>
              Total: {currency} {money(quote.total)}
            </span>
          </div>
          <div style={{ marginTop: '6px', fontSize: '13px', color: '#475569' }}>
            Classes: {(quote.classes || []).length > 0
              ? (quote.classes || []).map((c) => `${c.classNumber ?? c.number}${(c.className || c.name) ? ` (${c.className || c.name})` : ''}`).join(', ')
              : (quote.classCount || '?')}
          </div>
          {quote.validation?.mismatch && (quote.manualPrice ? (
            <p style={{ color: '#64748b', fontSize: '12px', margin: '6px 0 0' }}>
              Custom price. The standard price for this scope is {currency} {money(quote.validation.expectedTotal)},
              and {currency} {money(quote.total)} is what the client is charged.
            </p>
          ) : (
            <p style={{ color: '#dc2626', fontSize: '12px', margin: '6px 0 0' }}>
              Stored total does not match the standard price for this scope (expected {currency} {money(quote.validation.expectedTotal)}). Review before sending.
            </p>
          ))}
          <PicklistWarning classes={quote.classes} onFix={runFixTerms} fixing={fixTerms.isPending} />
        </div>
      )}

      {/* An unsent revision is the client waiting on the updated quote they asked for */}
      {report.quoteRevisionPending && alreadySent && !draft && (
        <div style={{ marginTop: '12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: '#92400e' }}>
            Revision {revision} has not been sent yet. The client still has the earlier quote.
          </span>
          <button
            className="mkt-btn mkt-btn-primary"
            disabled={sendRevision.isPending}
            onClick={() => setConfirmSend(true)}
          >
            {sendRevision.isPending ? 'Sending...' : 'Send updated quote'}
          </button>
        </div>
      )}

      {sendResult && (
        <p style={{ marginTop: '10px', fontSize: '13px', color: '#047857' }}>
          Updated quote {sendResult.quoteNumber} emailed to {sendResult.email}.
        </p>
      )}

      {confirmSend && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '440px', width: '90%' }}>
            <h3 style={{ margin: '0 0 8px' }}>Send the updated quote?</h3>
            <p style={{ color: '#475569', fontSize: '14px' }}>
              This emails the revised quote to <strong>{report.contact?.email}</strong> with the new
              PDF attached and a sign-in link to their dashboard, where the new total is what they
              will be charged. The report itself is not resent, and the pipeline stage does not change.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button className="mkt-btn mkt-btn-secondary" onClick={() => setConfirmSend(false)}>Cancel</button>
              <button
                className="mkt-btn mkt-btn-primary"
                disabled={sendRevision.isPending}
                onClick={doSendRevision}
              >
                {sendRevision.isPending ? 'Sending...' : 'Send it'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Fee risk, in plain terms: we quote IP Australia's $250/class picklist rate,
 * but a class containing wording that isn't an exact picklist entry is filed
 * at $400/class. The difference comes out of our margin, so it has to be
 * visible before the quote goes out rather than discovered at filing.
 */
function PicklistWarning({ classes, onFix, fixing }) {
  const list = classes || [];
  const failing = list.filter((c) => c.picklistVerified === false);
  const unknown = list.filter((c) => c.picklistVerified == null && (c.goodsServices || []).length === 0);

  if (failing.length === 0 && unknown.length === 0) {
    if (list.length > 0 && list.every((c) => c.picklistVerified === true)) {
      return (
        <p style={{ color: '#047857', fontSize: '12px', margin: '6px 0 0' }}>
          ✓ All goods and services are exact IP Australia picklist terms ($250 per class).
        </p>
      );
    }
    return null;
  }

  const extraCost = failing.length * 150;
  return (
    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 12px', margin: '8px 0 0' }}>
      {failing.length > 0 && (
        <>
          <p style={{ color: '#92400e', fontSize: '12px', margin: 0, fontWeight: 600 }}>
            {failing.length} class{failing.length > 1 ? 'es' : ''} contain{failing.length > 1 ? '' : 's'} wording
            that is not on the IP Australia picklist, so filing costs $400 per class instead of $250
            (about ${extraCost} more, which we absorb).
          </p>
          {failing.map((c) => (
            <p key={c.classNumber} style={{ color: '#92400e', fontSize: '11px', margin: '4px 0 0' }}>
              Class {c.classNumber}: {(c.nonPicklistTerms || []).map((t) => (
                t.suggestion ? `"${t.term}" (closest: "${t.suggestion}")` : `"${t.term}"`
              )).join(', ')}
            </p>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
            <button
              className="mkt-btn mkt-btn-secondary"
              style={{ fontSize: '12px' }}
              disabled={fixing}
              onClick={onFix}
            >
              {fixing ? 'Finding picklist wording...' : '🛠 Fix wording for me'}
            </button>
            <span style={{ color: '#92400e', fontSize: '11px' }}>
              Picks the closest picklist term for each, for you to review before saving.
            </span>
          </div>
        </>
      )}
      {unknown.length > 0 && (
        <p style={{ color: '#92400e', fontSize: '11px', margin: failing.length ? '6px 0 0' : 0 }}>
          No goods or services recorded for class{unknown.length > 1 ? 'es' : ''}{' '}
          {unknown.map((c) => c.classNumber).join(', ')}, so the filing wording still has to be chosen.
        </p>
      )}
    </div>
  );
}

/** Searchable list of the 45 NICE classes. */
function ClassPicker({ existing, onPick }) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useNiceClasses(true);
  const classes = data?.classes || [];
  const term = search.trim().toLowerCase();
  const filtered = term
    ? classes.filter((c) =>
      String(c.classNumber) === term ||
      (c.heading || '').toLowerCase().includes(term) ||
      (c.shortName || '').toLowerCase().includes(term))
    : classes;

  return (
    <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px', margin: '6px 0', background: '#f8fafc' }}>
      <input
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search classes, e.g. retail or 35"
        style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', marginBottom: '8px' }}
      />
      {isLoading && <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>Loading classes...</p>}
      <div style={{ maxHeight: '220px', overflowY: 'auto', display: 'grid', gap: '4px' }}>
        {filtered.map((c) => {
          const already = existing.includes(c.classNumber);
          return (
            <button
              key={c.classNumber}
              disabled={already}
              onClick={() => onPick(c)}
              style={{
                textAlign: 'left', border: '1px solid #e2e8f0', background: already ? '#f1f5f9' : 'white',
                borderRadius: '6px', padding: '6px 10px', cursor: already ? 'default' : 'pointer',
                color: already ? '#94a3b8' : '#334155', fontSize: '12px',
              }}
            >
              <strong>Class {c.classNumber}</strong> · {c.shortName}
              {already && ' (already on the quote)'}
              <span style={{ display: 'block', color: '#94a3b8', fontSize: '11px' }}>{(c.heading || '').slice(0, 110)}...</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Goods/services for one class.
 *
 * Terms are chosen from the IP AUSTRALIA PICKLIST, not typed. That is a
 * margin decision: IP Australia charges $250/class when every term is an
 * exact picklist entry and $400/class when any term is not, and our quote
 * always assumes $250. "Suggest with AI" proposes terms for this specific
 * applicant and snaps them to the picklist before they are offered.
 */
function TermsEditor({ classNumber, terms, onChange, reportId }) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiError, setAiError] = useState(null);
  const suggest = useSuggestGoodsServices();

  // Each search scans thousands of picklist terms server-side, so wait for a
  // pause in typing rather than firing a request per keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isFetching } = useNiceClassTerms(classNumber, debouncedSearch || undefined);

  const picklistTerms = (data?.terms || []).filter((t) => !terms.includes(t)).slice(0, 15);
  const totalMatches = data?.termCount ?? null;

  const runSuggest = () => {
    setAiError(null);
    suggest.mutate(
      { reportId, classNumber },
      {
        onSuccess: (res) => setAiSuggestions(res),
        onError: (e) => setAiError(e.response?.data?.error || e.message),
      }
    );
  };

  const addTerm = (t) => {
    if (terms.includes(t)) return;
    onChange([...terms, t]);
  };

  return (
    <div style={{ marginTop: '10px', borderTop: '1px dashed #e2e8f0', paddingTop: '10px' }}>
      <textarea
        rows={Math.min(8, Math.max(3, terms.length + 1))}
        value={terms.join('\n')}
        onChange={(e) => onChange(e.target.value.split('\n').map((t) => t.trim()).filter(Boolean))}
        placeholder="One term per line. Pick from the picklist below rather than typing, or the filing costs $400/class instead of $250."
        style={{ width: '100%', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit' }}
      />

      <div style={{ display: 'flex', gap: '6px', marginTop: '6px', alignItems: 'center' }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search the IP Australia picklist for class ${classNumber}`}
          style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
        />
        <button
          className="mkt-btn mkt-btn-secondary"
          style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
          disabled={suggest.isPending}
          onClick={runSuggest}
        >
          {suggest.isPending ? 'Thinking...' : '✨ Suggest with AI'}
        </button>
      </div>

      {totalMatches != null && (
        <p style={{ fontSize: '11px', color: '#94a3b8', margin: '4px 0 0' }}>
          {totalMatches.toLocaleString()} picklist term{totalMatches === 1 ? '' : 's'}
          {debouncedSearch ? ` match "${debouncedSearch}"` : ` in class ${classNumber}`}
          {data?.truncated ? ', showing the closest' : ''}
        </p>
      )}

      {aiError && <p style={{ fontSize: '12px', color: '#dc2626', margin: '6px 0 0' }}>{aiError}</p>}

      {aiSuggestions && (
        <div style={{ marginTop: '8px', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', padding: '8px 10px' }}>
          <p style={{ fontSize: '11px', color: '#0f766e', margin: '0 0 6px', fontWeight: 600 }}>
            Suggested for this business{aiSuggestions.rationale ? `: ${aiSuggestions.rationale}` : ''}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {aiSuggestions.suggestions.filter((s) => !terms.includes(s.term)).map((s) => (
              <button
                key={s.term}
                onClick={() => addTerm(s.term)}
                title="Exact IP Australia picklist term"
                style={{ border: '1px solid #0f766e', background: 'white', borderRadius: '999px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', color: '#0f766e' }}
              >
                + {s.term}
              </button>
            ))}
            {aiSuggestions.suggestions.length > 0
              && aiSuggestions.suggestions.every((s) => terms.includes(s.term)) && (
              <span style={{ fontSize: '11px', color: '#0f766e' }}>All ready-to-use suggestions added.</span>
            )}
            {aiSuggestions.suggestions.length === 0 && (
              <span style={{ fontSize: '11px', color: '#0f766e' }}>
                Nothing matched the picklist exactly, so pick from the options below.
              </span>
            )}
          </div>

          {/* Terms with no exact picklist entry: the picklist wants a qualifier
              ("retail services for clothing"), and only a human knows which one */}
          {(aiSuggestions.needsChoice || []).map((n) => (
            <div key={n.aiTerm} style={{ marginTop: '8px', borderTop: '1px dashed #99f6e4', paddingTop: '6px' }}>
              <p style={{ fontSize: '11px', color: '#0f766e', margin: '0 0 4px' }}>
                &ldquo;{n.aiTerm}&rdquo; is not a picklist term on its own.
                {n.searchQuery && n.searchQuery !== n.aiTerm
                  ? ` Closest ${n.optionCount.toLocaleString()} for "${n.searchQuery}":`
                  : ` Closest of ${n.optionCount.toLocaleString()}:`}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {n.options.filter((o) => !terms.includes(o)).map((o) => (
                  <button
                    key={o}
                    onClick={() => addTerm(o)}
                    style={{ border: '1px solid #cbd5e1', background: 'white', borderRadius: '999px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', color: '#475569' }}
                  >
                    + {o}
                  </button>
                ))}
                <button
                  onClick={() => setSearch(n.searchQuery || n.aiTerm)}
                  style={{ border: 'none', background: 'transparent', fontSize: '11px', cursor: 'pointer', color: '#0f766e', textDecoration: 'underline' }}
                >
                  search all {n.optionCount.toLocaleString()}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
        {isFetching && <span style={{ fontSize: '11px', color: '#94a3b8' }}>Searching...</span>}
        {picklistTerms.map((t) => (
          <button
            key={t}
            onClick={() => addTerm(t)}
            style={{ border: '1px solid #cbd5e1', background: 'white', borderRadius: '999px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', color: '#475569' }}
          >
            + {t}
          </button>
        ))}
      </div>
    </div>
  );
}

export default QuoteEditor;
